/**
 * API Response Interceptor
 *
 * Automatically normalizes all URLs in API responses to ensure consistent environment handling.
 * This solves the structural issue where backend returns production URLs in all environments.
 *
 * 🔧 ENHANCED: Added cross-environment data validation to prevent data leakage
 */

import { axiosClient, publicAxiosClient } from '../http/axiosClient';
import { urlNormalizationService } from '../services/urlNormalizationService';
import {
  validateEnvironmentData,
  logValidationViolations,
  determineValidationAction,
} from './environmentDataValidator';

// Track interceptor statistics
let interceptorStats = {
  totalRequests: 0,
  normalizedResponses: 0,
  errors: 0,
  violationsDetected: 0,
  violationsBlocked: 0,
};

/**
 * Response interceptor that normalizes URLs in all API responses
 * 🔧 ENHANCED: Now validates environment data before normalization
 */
const responseInterceptor = (response: any): any => {
  try {
    interceptorStats.totalRequests++;

    // Only process successful responses with data
    if (response.status >= 200 && response.status < 300 && response.data) {
      const session = urlNormalizationService.getSession();
      const originalData = response.data;

      // 🔧 CRITICAL FIX: Validate data matches expected environment BEFORE normalization
      // This detects cross-environment data leakage early
      const validation = validateEnvironmentData(originalData, session.environment);

      if (!validation.valid) {
        interceptorStats.violationsDetected++;

        // Log violations
        logValidationViolations(validation, response.config.url || 'unknown');

        // Determine action based on environment
        const action = determineValidationAction(validation, session.environment);

        if (action === 'block') {
          // Production: Block cross-environment data
          interceptorStats.violationsBlocked++;

          throw new Error(
            `Cross-environment data leakage detected: Expected ${session.environment} data, ` +
            `but found ${validation.violationCount} violation(s). ` +
            `This is a critical security issue. Please refresh the page.`
          );
        } else if (action === 'normalize') {
          // Development/Staging: Normalize and warn
          console.warn(
            `⚠️ [API INTERCEPTOR] Cross-environment data detected in ${session.environment} - normalizing automatically`,
            {
              violations: validation.violations,
              url: response.config.url,
            }
          );
        }
      }

      // Normalize URLs (either after validation passed, or in permissive mode)
      const normalizedData =
        urlNormalizationService.normalizeApiResponse(originalData);

      // Only count as normalized if data actually changed
      if (normalizedData !== originalData) {
        interceptorStats.normalizedResponses++;

        console.log('🔄 [API INTERCEPTOR] Normalized response from:', {
          url: response.config.url,
          method: response.config.method?.toUpperCase(),
          dataType: Array.isArray(originalData?.data)
            ? 'array'
            : typeof originalData?.data,
          environment: session.environment,
          hadViolations: !validation.valid,
        });
      }

      // Update response data
      response.data = normalizedData;
    }

    return response;
  } catch (error) {
    interceptorStats.errors++;
    console.error('❌ [API INTERCEPTOR] Error processing response:', error);

    // If it's a validation error, re-throw it to show user
    if (error instanceof Error && error.message.includes('Cross-environment data leakage')) {
      throw error;
    }

    // Return original response for other errors
    return response;
  }
};

/**
 * Error interceptor for logging and debugging
 */
const errorInterceptor = (error: any) => {
  interceptorStats.errors++;

  // Log CORS errors specifically since they're related to URL issues
  if (error.message && error.message.includes('CORS')) {
    console.error('🚨 [API INTERCEPTOR] CORS error detected:', {
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
      environment: urlNormalizationService.getSession().environment,
      message: error.message,
    });
  }

  return Promise.reject(error);
};

/**
 * Setup API interceptors for URL normalization
 */
export function setupApiInterceptors(): void {
  // Add response interceptor to shared clients
  axiosClient.interceptors.response.use(responseInterceptor, errorInterceptor);
  publicAxiosClient.interceptors.response.use(
    responseInterceptor,
    errorInterceptor
  );

  console.log('🔧 [API INTERCEPTOR] URL normalization interceptors installed');

  // Perform environment handshake
  urlNormalizationService.performEnvironmentHandshake().then(success => {
    if (success) {
      console.log('🤝 [API INTERCEPTOR] Environment handshake completed');
    }
  });
}

/**
 * Get interceptor statistics
 */
export function getInterceptorStats() {
  return {
    ...interceptorStats,
    normalizationRate:
      interceptorStats.totalRequests > 0
        ? Math.round(
            (interceptorStats.normalizedResponses /
              interceptorStats.totalRequests) *
              100
          )
        : 0,
    session: urlNormalizationService.getSession(),
  };
}

/**
 * Reset interceptor statistics (useful for testing)
 */
export function resetInterceptorStats(): void {
  interceptorStats = {
    totalRequests: 0,
    normalizedResponses: 0,
    errors: 0,
    violationsDetected: 0,
    violationsBlocked: 0,
  };
}

/**
 * Manual URL normalization for cases where interceptor doesn't apply
 * (e.g., URLs from local storage, props, etc.)
 */
export function normalizeUrlManually(
  url: string | null | undefined
): string | null {
  return urlNormalizationService.normalizeUrl(url);
}

export default {
  setup: setupApiInterceptors,
  getStats: getInterceptorStats,
  resetStats: resetInterceptorStats,
  normalizeUrl: normalizeUrlManually,
};
